import { ObjectWithTranslation, Occupation } from 'common/types/transformedRespTypes'
import {
  ContractAuthAppendixStatus,
  ContractAuthRequestStatus,
  LaborerSalaryChangeRequestStatus,
} from 'common/types/commonTypes'
import { GetLaborerSubmitedRatingsResponse } from 'api/laborerPortalAPI/authContract'

import { groupBy } from '../common'

export type LaborerAuthRequestFromBE = {
  id: number
  created_at: string
  employer: {
    id_number: string
    name: string
    nationality: string
    job: string
    mobile_number: string
  }
  iqama_number: string
  name: string
  name_en: string
  new_salary: string
  duration_in_months: number
  nationality?: {
    label: string
    label_en: string
  } | null
  occupation: {
    label: string
    label_en: string
  }
  start_date: string
  status: ContractAuthRequestStatus
  passport_number: string
  residency: {
    description: string | null
  }
  legalText: {
    ar_file_id: string
    en_file_id: string
  } | null
}

export type LaborerAuthRequest = {
  id: number
  createdAt: string
  employer: {
    idNumber: string
    name: string
    nationality: string
    job: string
    mobileNumber: string
  }
  iqamaNumber: string
  duration: number
  name: ObjectWithTranslation
  newSalary: string
  nationality?: ObjectWithTranslation
  occupation: Occupation
  startDate: string
  status: ContractAuthRequestStatus
  passportNumber?: string
  residency?: string
  legalText: {
    pdfIdAr: string
    pdfIdEn: string
  } | null
}

export const getEmptyLaborerAuthRequest = (): LaborerAuthRequest => ({
  id: 0,
  createdAt: '',
  employer: {
    idNumber: '',
    name: '',
    nationality: '',
    job: '',
    mobileNumber: '',
  },
  iqamaNumber: '',
  duration: 0,
  newSalary: '',
  name: {
    id: 0,
    label: '',
    labelEn: '',
  },
  nationality: {
    id: 0,
    label: '',
    labelEn: '',
  },
  occupation: {
    id: 0,
    label: '',
    labelEn: '',
  },
  startDate: '',
  status: ContractAuthRequestStatus.REQUEST_EXPIRED,
  passportNumber: '',
  residency: '',
  legalText: null,
})

export const convertAuthRequestFromResponse = (request: LaborerAuthRequestFromBE): LaborerAuthRequest => ({
  id: request.id,
  createdAt: request.created_at,
  employer: {
    idNumber: request.employer.id_number,
    name: request.employer.name,
    nationality: request.employer.nationality,
    job: request.employer.job,
    mobileNumber: request.employer.mobile_number,
  },
  iqamaNumber: request.iqama_number,
  duration: request.duration_in_months,
  name: {
    id: 0,
    label: request.name,
    labelEn: request.name_en,
  },
  newSalary: request.new_salary,
  nationality: request.nationality
    ? {
        id: 0,
        label: request.nationality.label,
        labelEn: request.nationality.label_en,
      }
    : undefined,
  occupation: {
    id: 0,
    label: request.occupation.label,
    labelEn: request.occupation.label_en,
  },
  startDate: request.start_date,
  status: request.status,
  passportNumber: request.passport_number || '',
  residency: request.residency?.description ? request.residency.description : '',
  legalText: request.legalText
    ? {
        pdfIdAr: request.legalText.ar_file_id,
        pdfIdEn: request.legalText.en_file_id,
      }
    : null,
})

export type LaborerAuthAppendixFromBE = {
  id: number
  authenticated_contract_id: number
  created_at: string
  status: ContractAuthAppendixStatus
  salary: string
  approved_at: string | null
  duration_in_months: number
  passport_number: string
  residency: {
    description: string | null
  }
}

export type LaborerAuthAppendix = {
  id: number
  contractId: number
  createdAt: string
  status: ContractAuthAppendixStatus
  salary: string
  approvedAt: string
  duration: number
  passportNumber?: string
  residency?: string
}

export const getEmptyLaborerAuthAppendix = (): LaborerAuthAppendix => ({
  id: 0,
  contractId: 0,
  createdAt: '',
  status: ContractAuthAppendixStatus.EXPIRED,
  salary: '',
  approvedAt: '',
  duration: 0,
  passportNumber: '',
  residency: '',
})

export const convertAuthAppendixFromResponse = (appendix: LaborerAuthAppendixFromBE): LaborerAuthAppendix => ({
  id: appendix.id,
  contractId: appendix.authenticated_contract_id,
  createdAt: appendix.created_at,
  status: appendix.status,
  salary: appendix.salary,
  approvedAt: appendix.approved_at || '',
  duration: appendix.duration_in_months,
  passportNumber: appendix.passport_number || '',
  residency: appendix.residency?.description ? appendix.residency.description : '',
})

export type LaborerSalaryChangeRequestFromBE = {
  id: number
  old_salary: number
  new_salary: number
  status: LaborerSalaryChangeRequestStatus
}

export type LaborerSalaryChangeRequest = {
  id: number
  oldSalary: number
  newSalary: number
  status: LaborerSalaryChangeRequestStatus
}

export const convertSalaryChangeRequestFromResponse = (
  request: LaborerSalaryChangeRequestFromBE,
): LaborerSalaryChangeRequest => ({
  id: request.id,
  oldSalary: request.old_salary,
  newSalary: request.new_salary,
  status: request.status,
})

export type LaborerSubmitedRatingInfo = {
  id: number
  employerName: string
  submissionDate: string
  starRatingValue: number
  answers: Record<string, number>
}

export const convertLaborerSubmitedRatings = (
  data: GetLaborerSubmitedRatingsResponse,
): Array<LaborerSubmitedRatingInfo> => {
  const grouped = Object.values(groupBy(data.data, (item) => item.period_start + item.period_end))
  return grouped.map((group) => {
    const rating = Math.round(group.reduce((acc, val) => acc + val.star_rating_value, 0) / group.length)
    const item = group[0]
    return {
      id: new Date().valueOf(),
      employerName: item.employer_name,
      submissionDate: item.submission_date,
      starRatingValue: rating,
      answers: Object.assign({}, ...group.map((currItem) => ({ [currItem.question_id]: currItem.star_rating_value }))),
    }
  })
}
